/**
 * Natural-language → RRULE converter for the SmartAdd `*` operator.
 *
 * Supports the v1 subset documented in recurrence.test.ts. The output is a
 * pair (rrule, fromCompletion):
 *  - rrule:           RFC 5545 RRULE string without DTSTART, suitable for rrule.js.
 *  - fromCompletion:  true if the next occurrence should be computed from the
 *                     completion date (the "after" form), false for fixed cadence
 *                     ("every"). RFC 5545 has no native concept for this, so we
 *                     keep it as a sibling flag and handle it in the recurrence
 *                     scheduler.
 */

export interface ParsedRecurrence {
  rrule: string;
  fromCompletion: boolean;
}

const WEEKDAY: Record<string, string> = {
  monday: 'MO',
  mon: 'MO',
  tuesday: 'TU',
  tue: 'TU',
  tues: 'TU',
  wednesday: 'WE',
  wed: 'WE',
  thursday: 'TH',
  thu: 'TH',
  thur: 'TH',
  thurs: 'TH',
  friday: 'FR',
  fri: 'FR',
  saturday: 'SA',
  sat: 'SA',
  sunday: 'SU',
  sun: 'SU',
};

const FREQ_FOR_UNIT: Record<string, string> = {
  day: 'DAILY',
  days: 'DAILY',
  week: 'WEEKLY',
  weeks: 'WEEKLY',
  month: 'MONTHLY',
  months: 'MONTHLY',
  year: 'YEARLY',
  years: 'YEARLY',
};

const NUMBER_WORDS: Record<string, number> = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
};

function parsePositiveCount(value: string): number | undefined {
  const numeric = Number.parseInt(value, 10);
  if (String(numeric) === value && numeric > 0) return numeric;
  return NUMBER_WORDS[value];
}

export function parseRecurrence(input: string): ParsedRecurrence | undefined {
  if (!input) return undefined;
  const s = input.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return undefined;

  // Single-word aliases: daily / weekly / monthly / yearly.
  const aliases: Record<string, string> = {
    daily: 'FREQ=DAILY',
    weekly: 'FREQ=WEEKLY',
    monthly: 'FREQ=MONTHLY',
    yearly: 'FREQ=YEARLY',
    annually: 'FREQ=YEARLY',
  };
  if (aliases[s]) return { rrule: aliases[s], fromCompletion: false };

  // every weekday
  if (s === 'every weekday') {
    return { rrule: 'FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR', fromCompletion: false };
  }

  // every <weekday>
  const everyWeekday = /^every (\w+)$/.exec(s);
  if (everyWeekday) {
    const word = everyWeekday[1] as string;
    if (WEEKDAY[word]) {
      return { rrule: `FREQ=WEEKLY;BYDAY=${WEEKDAY[word]}`, fromCompletion: false };
    }
    if (FREQ_FOR_UNIT[word]) {
      return { rrule: `FREQ=${FREQ_FOR_UNIT[word]}`, fromCompletion: false };
    }
  }

  // every N <unit>
  const everyN =
    /^every (\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve) (\w+)$/.exec(s);
  if (everyN) {
    const n = parsePositiveCount(everyN[1] as string);
    const unit = everyN[2] as string;
    const freq = FREQ_FOR_UNIT[unit];
    if (freq && n !== undefined) {
      return { rrule: `FREQ=${freq};INTERVAL=${n}`, fromCompletion: false };
    }
  }

  // after N <unit>
  const afterN =
    /^after (\d+|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve) (\w+)$/.exec(s);
  if (afterN) {
    const n = parsePositiveCount(afterN[1] as string);
    const unit = afterN[2] as string;
    const freq = FREQ_FOR_UNIT[unit];
    if (freq && n !== undefined) {
      return { rrule: `FREQ=${freq};INTERVAL=${n}`, fromCompletion: true };
    }
  }

  // after a/an <unit>
  const afterOne = /^after an? (\w+)$/.exec(s);
  if (afterOne) {
    const unit = afterOne[1] as string;
    const freq = FREQ_FOR_UNIT[unit];
    if (freq) return { rrule: `FREQ=${freq}`, fromCompletion: true };
  }

  return undefined;
}
